import React, {
  ChangeEvent, useCallback, useEffect, useState,
} from 'react';
import classNames from 'classnames';

import { MAX_QTY, MIN_QTY } from 'containers/KitWorkspaceStorage/types';
import styles from './QtyStepper.module.scss';

interface IQtyStepperProps {
  value: number;
  onChange: (next: number) => void;
  disabled?: boolean;
  className?: string;
  ariaLabel?: string;
}

const clamp = (n: number): number => Math.min(MAX_QTY, Math.max(MIN_QTY, n));

/**
 * Tap +/- stepper with direct numeric entry, clamped 1-999. 44px tap targets
 * for mobile. Commits on change/blur, never emits an out-of-range value.
 */
const QtyStepper = ({
  value, onChange, disabled = false, className, ariaLabel = 'Quantity',
}: IQtyStepperProps) => {
  const [text, setText] = useState<string>(String(value));

  useEffect(() => { setText(String(value)); }, [value]);

  const commit = useCallback((n: number) => {
    const next = clamp(n);
    onChange(next);
    setText(String(next));
  }, [onChange]);

  const onInput = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    const raw = e.currentTarget.value;
    setText(raw);
    const n = Number(raw);
    if (raw !== '' && !Number.isNaN(n)) {
      const next = clamp(n);
      if (next === n) onChange(next);
    }
  }, [onChange]);

  const onBlur = useCallback(() => {
    const n = Number(text);
    commit(Number.isNaN(n) || text === '' ? MIN_QTY : n);
  }, [text, commit]);

  return (
    <div className={classNames(styles.stepper, className)}>
      <button
        type="button"
        className={styles.btn}
        onClick={() => commit(value - 1)}
        disabled={disabled || value <= MIN_QTY}
        aria-label="Decrease quantity"
      >
        &#x2212;
      </button>
      <input
        className={styles.input}
        type="number"
        inputMode="numeric"
        min={MIN_QTY}
        max={MAX_QTY}
        value={text}
        onChange={onInput}
        onBlur={onBlur}
        disabled={disabled}
        aria-label={ariaLabel}
      />
      <button
        type="button"
        className={styles.btn}
        onClick={() => commit(value + 1)}
        disabled={disabled || value >= MAX_QTY}
        aria-label="Increase quantity"
      >
        &#x2b;
      </button>
    </div>
  );
};

export default QtyStepper;
